import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';
import { authService } from '../../features/auth/services/authService';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import PasswordStrengthChecker, { isPasswordStrong } from '../../features/sharedComponents/PasswordStrengthChecker';

const AccountRecovery: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Logic State - RETAINED
    const [code, setCode] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isValidCode, setIsValidCode] = useState(false);

    const [view, setView] = useState<'initiate' | 'verifying' | 'reset_form'>('initiate');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlCode = queryParams.get('code');

        if (urlCode) {
            setCode(urlCode);
            setView('verifying');
            verifyCode(urlCode);
        }
    }, [location.search]);

    const verifyCode = async (paramsCode: string) => {
        try {
            const response = await authService.verifyResetCode(paramsCode);
            if (response.isSuccess) {
                setUserId(response.data as string);
                setIsValidCode(true);
                setView('reset_form');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Link',
                    text: response.message || 'This password reset link is invalid or has expired.',
                    confirmButtonText: 'Try Again',
                    confirmButtonColor: '#00A63E'
                }).then(() => {
                    navigate('/reset-password');
                    setView('initiate');
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Link',
                text: error?.response?.data?.message || 'Failed to verify link.',
                confirmButtonText: 'Try Again',
                confirmButtonColor: '#00A63E'
            }).then(() => {
                navigate('/reset-password');
                setView('initiate');
            });
        }
    };

    const handleInitiateReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authService.initiatePasswordReset(email);
            Swal.fire({
                icon: 'success',
                title: 'Check your email',
                text: `We have sent password reset instructions to ${email}`,
                confirmButtonColor: '#00A63E'
            });
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.response?.data?.message || 'Failed to send reset email.',
                confirmButtonColor: '#00A63E'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            Swal.fire({
                icon: 'warning',
                title: 'Passwords do not match',
                text: 'Please ensure both passwords match.',
                confirmButtonColor: '#00A63E'
            });
            return;
        }
        if (!userId) return;

        setLoading(true);
        try {
            const response = await authService.completePasswordReset({
                userId,
                newPassword: password
            });

            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Password Reset Successful',
                    text: 'You can now log in with your new password.',
                    confirmButtonColor: '#00A63E'
                }).then(() => {
                    navigate('/login');
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to reset password.',
                    confirmButtonColor: '#00A63E'
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.response?.data?.message || 'Failed to reset password.',
                confirmButtonColor: '#00A63E'
            });
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const inputClass = "w-full bg-[#F3F4F6] border-none rounded-md py-3 px-4 text-gray-700 focus:ring-2 focus:ring-[#00A63E] outline-none transition-all placeholder:text-gray-400";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

    return (
        <div className="flex min-h-screen bg-white">
            {/* LEFT SIDE - BRANDING */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#00A63E] flex-col justify-between p-16 text-white sticky top-0 h-screen">
                <div>
                    <div className="flex items-center gap-3">
                        <img src={TetfundLogo} alt="Logo" className="bg-white p-1.5 rounded h-10" />
                        <span className="text-xl font-medium tracking-wide">TAG Portal</span>
                    </div>
                </div>
                <div className="max-w-md">
                    <h1 className="text-5xl font-normal leading-[1.15] mb-6">Account Recovery</h1>
                    <p className="text-lg opacity-80 leading-relaxed font-light">
                        {view === 'reset_form'
                            ? "Create a new strong password to secure your account."
                            : "Don't worry, it happens to the best of us. We'll help you recover your account."}
                    </p>
                </div>
                <p className="text-sm opacity-70">© {new Date().getFullYear()} TETFund TAG. All rights reserved.</p>
            </div>

            {/* RIGHT SIDE - FORM AREA */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16">
                <div className="w-full max-w-md">

                    {/* VERIFYING VIEW */}
                    {view === 'verifying' && (
                        <div className="text-center animate-pulse">
                            <div className="w-16 h-16 border-4 border-[#00A63E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <h2 className="text-2xl font-bold text-gray-900">Verifying Link...</h2>
                            <p className="text-gray-500">Please wait while we validate your reset link.</p>
                        </div>
                    )}

                    {/* INITIATE VIEW */}
                    {view === 'initiate' && (
                        <div className="animate-in fade-in duration-500">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot password?</h2>
                            <p className="text-gray-500 mb-8">No worries, we'll send you reset instructions.</p>

                            <form onSubmit={handleInitiateReset} className="space-y-6">
                                <div>
                                    <label className={labelClass}>Email address</label>
                                    <input
                                        type="email"
                                        placeholder="name@university.edu"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={inputClass}
                                        required
                                    />
                                </div>

                                <button type="submit" disabled={loading} className={`w-full py-3.5 rounded-lg font-semibold transition-all ${email ? 'bg-[#00A63E] text-white' : 'bg-[#A7F3D0] text-white cursor-not-allowed'}`}>
                                    {loading ? 'Sending...' : 'Reset password'}
                                </button>

                                <div className="text-center mt-6">
                                    <Link to="/login" className="text-sm font-medium text-gray-500 hover:text-[#00A63E] flex items-center justify-center gap-2">
                                        <i className="bi bi-arrow-left"></i> Back to login
                                    </Link>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* RESET FORM VIEW */}
                    {view === 'reset_form' && (
                        <div className="animate-in fade-in duration-500">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Set new password</h2>
                            <p className="text-gray-500 mb-8">Enter your new secure password below.</p>

                            <form onSubmit={handleCompleteReset} className="space-y-5">
                                <div className="relative">
                                    <label className={labelClass}>New password</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter new password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={inputClass}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            onClick={togglePasswordVisibility}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    <PasswordStrengthChecker password={password} />
                                </div>

                                <div className="relative">
                                    <label className={labelClass}>Confirm password</label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="Re-enter password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={inputClass}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                            onClick={toggleConfirmPasswordVisibility}
                                        >
                                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    {confirmPassword && password !== confirmPassword && (
                                        <span className="text-xs text-red-500 mt-1 block">Passwords do not match</span>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !password || password !== confirmPassword || !isPasswordStrong(password)}
                                    className={`w-full py-3.5 rounded-lg font-semibold transition-all mt-4 
                    ${password && password === confirmPassword && isPasswordStrong(password) ? 'bg-[#00A63E] text-white hover:bg-[#008c34]' : 'bg-[#A7F3D0] text-white cursor-not-allowed'}`}
                                >
                                    {loading ? 'Resetting...' : 'Reset password'}
                                </button>

                                <div className="text-center mt-6">
                                    <Link to="/login" className="text-sm font-medium text-gray-500 hover:text-[#00A63E] flex items-center justify-center gap-2">
                                        <ArrowLeft size={16} /> Back to login
                                    </Link>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountRecovery;